// Trusted, code-authored article figures (hand-drawn SVG infographics).
//
// Why a registry instead of inline SVG in the markdown? The article markdown is
// fully HTML-escaped before rendering (see ui/markdown.js), so raw <svg> in a
// .md file would render as literal text. Figures are *trusted* visuals authored
// here in code, never user content, so they're safe to inject as innerHTML.
//
// An article references a figure with a line on its own:
//
//     [[figure:preterite-imperfect-timeline]]
//
// markdown.js turns that into <div data-figure="ID">, and article.js swaps in
// the SVG from this map after rendering. Each entry is a zero-arg function so a
// figure can be lazy/parameterised later without changing call sites.
//
// Accessibility rules followed by every figure (from the v2 pedagogy research):
//   • role="img" + <title>/<desc> so screen readers get a real description;
//   • colour is NEVER the only cue — shape, texture (dash), and text labels
//     each independently carry the imperfetto-vs-passato-prossimo distinction;
//   • any motion sits behind `@media (prefers-reduced-motion: no-preference)`
//     so the default, reduced-motion render is fully static.

// ---- Passato Prossimo vs Imperfetto timeline ----------------------------
//
// The flagship A2 visual. It dramatises one sentence —
//   "Era notte e pioveva quando, all'improvviso, è squillato il telefono."
// — as a timeline: the imperfetto is a continuous striped ribbon (the film
// still rolling, no edges); the passato prossimo is a single solid pin dropped
// onto it (a bounded, framed photo) that interrupts the ongoing scene.
function preteriteImperfectTimeline() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 300" role="img"
         aria-labelledby="ptimp-title ptimp-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="ptimp-title">Passato prossimo versus imperfetto on a timeline</title>
      <desc id="ptimp-desc">A left-to-right timeline of the past. A long striped
        green ribbon labelled imperfetto runs across the whole timeline,
        standing for the ongoing background — it was night, it was raining, I
        was reading. A single solid amber pin labelled passato prossimo, è
        squillato, drops onto the ribbon partway along, standing for the one
        bounded event that interrupts the scene: the phone rang.</desc>

      <style>
        .ptimp-axis { stroke: #9ca89c; stroke-width: 2; }
        .ptimp-tick-label { fill: #6b766b; font-size: 13px; font-family: Inter, system-ui, sans-serif; }
        .ptimp-ribbon { fill: #cfe0cf; stroke: #5f7a5f; stroke-width: 1.5; }
        .ptimp-ribbon-stripes { stroke: #5f7a5f; stroke-width: 1.5; opacity: 0.55; }
        .ptimp-label-imp { fill: #3f553f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .ptimp-sub-imp { fill: #5f7a5f; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .ptimp-pin { fill: #d9803a; stroke: #ffffff; stroke-width: 2.5; }
        .ptimp-pin-stem { stroke: #b9682c; stroke-width: 3; }
        .ptimp-label-pret { fill: #9a531f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .ptimp-sub-pret { fill: #b9682c; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .ptimp-quote { fill: #3f553f; font-size: 14px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .ptimp-draw { stroke-dasharray: 560; stroke-dashoffset: 560; }
        @media (prefers-reduced-motion: no-preference) {
          .ptimp-draw { animation: ptimp-dash 1.1s ease-out forwards; }
          .ptimp-pin-pop { transform-box: fill-box; transform-origin: center bottom;
            animation: ptimp-pop 0.5s ease-out 0.9s both; }
        }
        @keyframes ptimp-dash { to { stroke-dashoffset: 0; } }
        @keyframes ptimp-pop { from { transform: scaleY(0); opacity: 0; } to { transform: scaleY(1); opacity: 1; } }
      </style>

      <!-- Scene caption -->
      <text x="30" y="34" class="ptimp-quote">“Era notte e pioveva quando, all'improvviso, è squillato il telefono.”</text>

      <!-- Imperfetto: a long striped ribbon = ongoing, edgeless background -->
      <g>
        <rect x="40" y="150" width="680" height="44" rx="22" class="ptimp-ribbon"/>
        <!-- diagonal stripes give the ribbon a texture cue independent of colour -->
        <g class="ptimp-ribbon-stripes">
          <line x1="70"  y1="152" x2="56"  y2="192"/>
          <line x1="130" y1="152" x2="116" y2="192"/>
          <line x1="190" y1="152" x2="176" y2="192"/>
          <line x1="250" y1="152" x2="236" y2="192"/>
          <line x1="430" y1="152" x2="416" y2="192"/>
          <line x1="490" y1="152" x2="476" y2="192"/>
          <line x1="550" y1="152" x2="536" y2="192"/>
          <line x1="610" y1="152" x2="596" y2="192"/>
          <line x1="670" y1="152" x2="656" y2="192"/>
        </g>
        <text x="56" y="138" class="ptimp-label-imp">imperfetto — the scene, still rolling</text>
        <text x="56" y="214" class="ptimp-sub-imp">era notte · pioveva · (io) leggevo — no clear start or end</text>
      </g>

      <!-- Timeline axis with past → ahora arrow -->
      <g>
        <line x1="40" y1="244" x2="724" y2="244" class="ptimp-axis ptimp-draw"/>
        <polygon points="724,244 712,239 712,249" fill="#9ca89c"/>
        <text x="40" y="266" class="ptimp-tick-label">passato</text>
        <text x="690" y="266" class="ptimp-tick-label">adesso</text>
      </g>

      <!-- Passato prossimo: one solid pin dropped onto the ribbon = a bounded event -->
      <g class="ptimp-pin-pop">
        <line x1="380" y1="172" x2="380" y2="120" class="ptimp-pin-stem"/>
        <circle cx="380" cy="172" r="13" class="ptimp-pin"/>
        <text x="380" y="108" text-anchor="middle" class="ptimp-label-pret">passato prossimo</text>
        <text x="380" y="92"  text-anchor="middle" class="ptimp-sub-pret">è squillato — one finished event</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      The ribbon keeps rolling (imperfetto); the pin marks the single, finished moment that cuts in (passato prossimo).
    </figcaption>
  </figure>`;
}

// ---- Per vs Da: directional arrows -------------------------------------
//
// A common confusion. The visual splits the field in two: PER points forward
// to a goal/destination (a single arrow aimed at a target flag); DA loops back
// to an origin and threads through a source/agent (a curved, dashed arrow that
// arcs through the middle). Direction (forward vs looping) and line texture
// (solid vs dashed) both carry the contrast independent of colour.
function porParaArrows() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 320" role="img"
         aria-labelledby="porpara-title porpara-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="porpara-title">Per versus da as two directions</title>
      <desc id="porpara-desc">Two contrasting arrows. On top, a straight solid
        amber arrow labelled per flies forward and lands on a goal flag,
        standing for purpose, destination and recipient — the aim ahead. Below,
        a curved dashed green arrow labelled da loops backward and threads
        through a source, standing for origin, the agent who did something, and
        the point in space or time that something comes from.</desc>

      <style>
        .pp-axis { stroke: #d8ddd8; stroke-width: 1.5; }
        .pp-para-line { stroke: #d9803a; stroke-width: 4; fill: none; }
        .pp-para-head { fill: #d9803a; }
        .pp-por-line { stroke: #5f7a5f; stroke-width: 4; fill: none; stroke-dasharray: 9 7; }
        .pp-por-head { fill: #5f7a5f; }
        .pp-label-para { fill: #9a531f; font-size: 19px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .pp-label-por  { fill: #3f553f; font-size: 19px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .pp-sense-para { fill: #b9682c; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .pp-sense-por  { fill: #5f7a5f; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .pp-eg { font-size: 13px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .pp-flag-pole { stroke: #9a531f; stroke-width: 3; }
        .pp-flag { fill: #f0c39a; stroke: #9a531f; stroke-width: 1.5; }
        .pp-draw { stroke-dashoffset: 520; stroke-dasharray: 520; }
        @media (prefers-reduced-motion: no-preference) {
          .pp-draw-para { animation: pp-dash 1s ease-out forwards; }
          .pp-draw-por  { animation: pp-dash 1s ease-out 0.35s forwards; }
        }
        @keyframes pp-dash { to { stroke-dashoffset: 0; } }
      </style>

      <line x1="40" y1="160" x2="720" y2="160" class="pp-axis"/>

      <!-- PARA: forward arrow to a goal flag -->
      <g>
        <text x="40" y="58" class="pp-label-para">per →</text>
        <text x="120" y="58" class="pp-sense-para">purpose · destination · recipient · duration</text>
        <path d="M 60 96 L 612 96" class="pp-para-line pp-draw pp-draw-para"/>
        <polygon points="612,96 596,87 596,105" class="pp-para-head"/>
        <!-- goal flag = the aim ahead -->
        <line x1="640" y1="70" x2="640" y2="120" class="pp-flag-pole"/>
        <polygon points="640,72 686,82 640,92" class="pp-flag"/>
        <text x="60" y="128" class="pp-eg" fill="#9a531f">“Studio per superare l'esame.” — I study in order to pass (the goal ahead).</text>
      </g>

      <!-- POR: curved dashed arrow looping back through a path -->
      <g>
        <text x="40" y="214" class="pp-label-por">↺ da</text>
        <text x="120" y="214" class="pp-sense-por">origin · source · agent (“by”) · starting point in time</text>
        <path d="M 612 250 C 470 300, 200 300, 70 252" class="pp-por-line pp-draw pp-draw-por"/>
        <polygon points="70,252 88,250 80,266" class="pp-por-head"/>
        <text x="60" y="300" class="pp-eg" fill="#3f553f">“Torno da Roma.” — I'm back from Rome (tracing back to where it started).</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      per aims forward at a goal (solid arrow → flag); da loops back to an origin or source (dashed arc).
    </figcaption>
  </figure>`;
}

// ---- Essere vs Stare: to be, two ways ----------------------------------
//
// Two columns. ESSERE is a carved stone plinth (identity, what a thing
// fundamentally IS — and the copula for most adjectives, sono stanco); STARE
// is a weather vane / changing sky (wellbeing, location, the ongoing now). The
// pairs (è attento / sta attento) sit across the gap to show a handful of
// adjectives swinging sense with the verb.
function serEstarEssenceState() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 340" role="img"
         aria-labelledby="serestar-title serestar-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="serestar-title">Essere versus stare — to be, two ways</title>
      <desc id="serestar-desc">Two columns. On the left, essere sits on a solid
        carved stone plinth standing for essence and identity — who or what
        something fundamentally is: origin, profession, description, and the
        copula for most adjectives. On the right, stare floats as a weather vane
        under a changing sky standing for wellbeing, location, the progressive
        and set phrases — how and where you are right now. Between them, three
        adjective pairs show the same word shifting meaning: attento is
        attentive with essere but pay attention with stare; buono is good or
        kind with essere but behave, stay good with stare; fermo is stopped or
        still with essere but hold still with stare.</desc>

      <style>
        .se-col-label-ser { fill: #3f553f; font-size: 21px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .se-col-label-est { fill: #9a531f; font-size: 21px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .se-tag { font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .se-plinth { fill: #cfe0cf; stroke: #5f7a5f; stroke-width: 2; }
        .se-plinth-line { stroke: #5f7a5f; stroke-width: 1.5; opacity: 0.5; }
        .se-vane-pole { stroke: #b9682c; stroke-width: 3; }
        .se-vane { fill: #f0c39a; stroke: #9a531f; stroke-width: 1.5; }
        .se-cloud { fill: #fbf0e6; stroke: #d9803a; stroke-width: 1.5; }
        .se-pair-es { fill: #3f553f; font-size: 13px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .se-pair-est { fill: #9a531f; font-size: 13px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .se-pair-word { fill: #6b766b; font-size: 13px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .se-divider { stroke: #e3e0d8; stroke-width: 1.5; stroke-dasharray: 4 5; }
        .se-vane-spin { transform-box: fill-box; transform-origin: bottom center; }
        @media (prefers-reduced-motion: no-preference) {
          .se-vane-spin { animation: se-sway 3.2s ease-in-out infinite; }
        }
        @keyframes se-sway { 0%,100% { transform: rotate(-7deg); } 50% { transform: rotate(7deg); } }
      </style>

      <!-- ESSERE: stone plinth = fixed essence -->
      <g>
        <text x="40" y="40" class="se-col-label-ser">essere — essence</text>
        <text x="40" y="60" class="se-tag" fill="#5f7a5f">identity · origin · profession · description</text>
        <rect x="70" y="120" width="150" height="120" rx="6" class="se-plinth"/>
        <line x1="70" y1="150" x2="220" y2="150" class="se-plinth-line"/>
        <line x1="70" y1="182" x2="220" y2="182" class="se-plinth-line"/>
        <line x1="70" y1="214" x2="220" y2="214" class="se-plinth-line"/>
        <text x="145" y="268" text-anchor="middle" class="se-tag" fill="#3f553f">“Sono di Roma.” · “È medica.”</text>
        <text x="145" y="288" text-anchor="middle" class="se-tag" fill="#3f553f">carved in stone — who &amp; what it is</text>
      </g>

      <!-- divider -->
      <line x1="380" y1="80" x2="380" y2="300" class="se-divider"/>

      <!-- STARE: weather vane under a changing sky = passing state -->
      <g>
        <text x="540" y="40" text-anchor="end" class="se-col-label-est">stare — how &amp; where</text>
        <text x="720" y="60" text-anchor="end" class="se-tag" fill="#b9682c">wellbeing · location · progressive · set phrases</text>
        <path d="M 560 120 q -14 -22 14 -24 q 6 -20 32 -8 q 22 -10 30 12 q 22 2 8 22 z" class="se-cloud"/>
        <line x1="610" y1="150" x2="610" y2="240" class="se-vane-pole"/>
        <g class="se-vane-spin">
          <polygon points="610,150 660,166 610,182" class="se-vane"/>
        </g>
        <text x="610" y="268" text-anchor="middle" class="se-tag" fill="#9a531f">“Come stai?” · “Sto a casa.”</text>
        <text x="610" y="288" text-anchor="middle" class="se-tag" fill="#9a531f">turns with the wind — how you are now</text>
      </g>

      <!-- meaning-shift pairs across the gap -->
      <g>
        <text x="380" y="120" text-anchor="middle" class="se-pair-word">attento</text>
        <text x="300" y="120" text-anchor="end" class="se-pair-es">è = attentive</text>
        <text x="460" y="120" class="se-pair-est">sta = pay attention</text>
        <text x="380" y="160" text-anchor="middle" class="se-pair-word">buono</text>
        <text x="300" y="160" text-anchor="end" class="se-pair-es">è = good / kind</text>
        <text x="460" y="160" class="se-pair-est">sta = behave!</text>
        <text x="380" y="200" text-anchor="middle" class="se-pair-word">fermo</text>
        <text x="300" y="200" text-anchor="end" class="se-pair-es">è = stopped</text>
        <text x="460" y="200" class="se-pair-est">sta = hold still</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      essere is the carved plinth (essence — who &amp; what something is); stare is the weather vane (wellbeing, place, the ongoing now). A few adjectives swing meaning between them.
    </figcaption>
  </figure>`;
}

// ---- Subjunctive trigger landscape -------------------------------------
//
// A decision-landscape for B2 mood choice. A central fork: every clause type
// faces one question — "asserted as fact, or doubt / opinion / not-yet-real?"
// Solid-ground boxes (indicative: certainty/known/conceded) sit on the left;
// misty, dashed-outline boxes (subjunctive: opinion/hypothetical/sought) sit
// on the right. The "always subjunctive" connectors get their own banner
// so learners don't have to weigh them. Outline texture (solid vs dashed) and
// labels carry the split independent of colour.
function subjunctiveTriggerLandscape() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 420" role="img"
         aria-labelledby="subjland-title subjland-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="subjland-title">The subjunctive-trigger decision landscape</title>
      <desc id="subjland-desc">A decision map for choosing mood in B2 clauses.
        One central question — is the situation asserted as fact, or coloured by
        doubt and opinion? — splits every clause type two ways. On the left,
        solid-outline boxes take the indicative: verbs of certainty, concessive
        clauses conceding a known fact with anche se, and relative clauses with a
        specific known antecedent. On the right, dashed-outline boxes take the
        subjunctive: verbs of opinion or doubt, concessive clauses with benché,
        and relative clauses whose antecedent is sought or unknown. A separate
        banner lists connectors that are always subjunctive: affinché, senza che,
        prima che, a meno che, purché, nel caso che.</desc>

      <style>
        .sl-q { fill: #3f553f; font-size: 16px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-col-ind { fill: #3f553f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-col-sub { fill: #9a531f; font-size: 15px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-box-ind { fill: #e7f0e7; stroke: #5f7a5f; stroke-width: 2; }
        .sl-box-sub { fill: #fbf0e6; stroke: #d9803a; stroke-width: 2; stroke-dasharray: 7 5; }
        .sl-box-title { font-size: 12.5px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .sl-box-eg { font-size: 11.5px; font-style: italic; font-family: Inter, system-ui, sans-serif; }
        .sl-fork { stroke: #9ca89c; stroke-width: 2; fill: none; }
        .sl-tag-ind { fill: #3f553f; font-size: 12px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .sl-tag-sub { fill: #9a531f; font-size: 12px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .sl-banner { fill: #cfe0cf; stroke: #5f7a5f; stroke-width: 1.5; }
        .sl-banner-text { fill: #3f553f; font-size: 12.5px; font-family: Inter, system-ui, sans-serif; }
        .sl-banner-title { fill: #3f553f; font-size: 13px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
      </style>

      <!-- central question + fork -->
      <text x="380" y="30" text-anchor="middle" class="sl-q">Asserted as fact?  ·  or doubt &amp; opinion?</text>
      <path d="M 380 42 L 380 64 M 380 64 C 380 80, 200 70, 200 92 M 380 64 C 380 80, 560 70, 560 92" class="sl-fork"/>
      <text x="150" y="84" class="sl-tag-ind">↙ asserted → indicative</text>
      <text x="470" y="84" class="sl-tag-sub">doubt → subjunctive ↘</text>

      <!-- column headers -->
      <text x="40" y="112" class="sl-col-ind">INDICATIVE — solid ground</text>
      <text x="720" y="112" text-anchor="end" class="sl-col-sub">SUBJUNCTIVE — doubt &amp; opinion</text>

      <!-- temporal -->
      <g>
        <rect x="40" y="124" width="320" height="56" rx="10" class="sl-box-ind"/>
        <text x="54" y="146" class="sl-box-title" fill="#3f553f">Main verb · certainty</text>
        <text x="54" y="166" class="sl-box-eg" fill="#5f7a5f">So che parte oggi.</text>
        <rect x="400" y="124" width="320" height="56" rx="10" class="sl-box-sub"/>
        <text x="414" y="146" class="sl-box-title" fill="#9a531f">Main verb · opinion / doubt</text>
        <text x="414" y="166" class="sl-box-eg" fill="#b9682c">Penso che parta oggi.</text>
      </g>

      <!-- concessive -->
      <g>
        <rect x="40" y="190" width="320" height="56" rx="10" class="sl-box-ind"/>
        <text x="54" y="212" class="sl-box-title" fill="#3f553f">Concessive · anche se (fact)</text>
        <text x="54" y="232" class="sl-box-eg" fill="#5f7a5f">Anche se è caro, lo compro.</text>
        <rect x="400" y="190" width="320" height="56" rx="10" class="sl-box-sub"/>
        <text x="414" y="212" class="sl-box-title" fill="#9a531f">Concessive · benché</text>
        <text x="414" y="232" class="sl-box-eg" fill="#b9682c">Benché sia caro, lo compro.</text>
      </g>

      <!-- relative -->
      <g>
        <rect x="40" y="256" width="320" height="56" rx="10" class="sl-box-ind"/>
        <text x="54" y="278" class="sl-box-title" fill="#3f553f">Relative · known antecedent</text>
        <text x="54" y="298" class="sl-box-eg" fill="#5f7a5f">Ho una casa che dà sul mare.</text>
        <rect x="400" y="256" width="320" height="56" rx="10" class="sl-box-sub"/>
        <text x="414" y="278" class="sl-box-title" fill="#9a531f">Relative · sought / unknown</text>
        <text x="414" y="298" class="sl-box-eg" fill="#b9682c">Cerco una casa che dia sul mare.</text>
      </g>

      <!-- always-subjunctive banner -->
      <g>
        <rect x="40" y="338" width="680" height="58" rx="12" class="sl-banner"/>
        <text x="60" y="362" class="sl-banner-title">Always subjunctive — no question needed:</text>
        <text x="60" y="384" class="sl-banner-text">affinché · senza che · prima che · a meno che · purché · nel caso che</text>
      </g>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      One question splits every clause: asserted as fact → indicative (solid boxes); doubt &amp; opinion → subjunctive (dashed boxes). A short list of connectors is always subjunctive.
    </figcaption>
  </figure>`;
}

// ---- -isc- verbs: the boot / shoe ---------------------------------------
//
// A classic A2 mnemonic. A 2-column, 3-row present-tense grid for capire (an
// -ire verb that inserts -isc-). Five of six forms are drawn, and a thick
// outline is wrapped around the four forms that take the -isc- infix — io, tu,
// lui/lei, loro — which together trace the shape of a boot: a tall "leg" down
// the left column and a "foot" kicking out to the right along the bottom row.
// The two cells left outside the boot (noi, voi) drop the infix because their
// stress falls on the ending. The pattern is carried three ways independent of
// colour: the boot outline (shape), a solid ▲ marker + "adds -isc-" caption on
// the four boot cells versus a hollow ○ + "no -isc-" on the two outside, and
// the infix set in bold.
function stemChangeBoot() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 400" role="img"
         aria-labelledby="boot-title boot-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="boot-title">The boot pattern of -isc- verbs</title>
      <desc id="boot-desc">A present-tense grid of capire, an -ire verb that
        inserts the -isc- infix, in two columns and three rows. A thick outline
        wraps four cells into the shape of a boot: the left column top to bottom
        — io capisco, tu capisci, lui or lei capisce — forms the tall leg, and
        the bottom-right cell, loro capiscono, forms the foot kicking out to the
        right. All four of these carry the -isc- infix, marked with a solid
        triangle and the caption adds -isc-. The two cells outside the boot, noi
        capiamo and voi capite at the top right, drop the infix, marked with a
        hollow circle and the caption no -isc-, because in those forms the stress
        falls on the ending, not the stem.</desc>

      <style>
        .boot-head { fill: #3f553f; font-size: 19px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .boot-sub { fill: #5f7a5f; font-size: 13px; font-family: Inter, system-ui, sans-serif; }
        .boot-fill { fill: #e7f0e7; }
        .boot-outline { fill: none; stroke: #3f553f; stroke-width: 3.5; stroke-linejoin: round; }
        .boot-cell-out { fill: #fbf0e6; stroke: #d9803a; stroke-width: 2; stroke-dasharray: 6 5; }
        .boot-sep { stroke: #9ac09a; stroke-width: 1.25; opacity: 0.7; }
        .boot-pron { fill: #6b766b; font-size: 12px; font-family: Inter, system-ui, sans-serif; }
        .boot-form { fill: #2f3f2f; font-size: 21px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .boot-vowel { fill: #9a531f; font-weight: 800; }
        .boot-tag-chg { fill: #3f553f; font-size: 11px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .boot-tag-reg { fill: #9a531f; font-size: 11px; font-weight: 600; font-family: Inter, system-ui, sans-serif; }
        .boot-marker-chg { fill: #3f553f; }
        .boot-marker-reg { fill: none; stroke: #b9682c; stroke-width: 1.75; }
      </style>

      <text x="40" y="36" class="boot-head">capire &nbsp;( + isc )</text>
      <text x="40" y="58" class="boot-sub">The -isc- infix appears in every form except noi / voi — the four -isc- forms trace a boot.</text>

      <!-- boot fill behind the four changing cells: left column (leg) + bottom-right (foot) -->
      <path d="M 140 90 L 380 90 L 380 230 L 620 230 L 620 300 L 140 300 Z" class="boot-fill"/>

      <!-- the two cells OUTSIDE the boot: noi & voi (top-right), no infix -->
      <rect x="380" y="90"  width="240" height="70" class="boot-cell-out"/>
      <rect x="380" y="160" width="240" height="70" class="boot-cell-out"/>

      <!-- internal cell separators -->
      <line x1="140" y1="160" x2="380" y2="160" class="boot-sep"/>
      <line x1="140" y1="230" x2="380" y2="230" class="boot-sep"/>
      <line x1="380" y1="300" x2="620" y2="300" class="boot-sep"/>

      <!-- boot outline drawn on top -->
      <path d="M 140 90 L 380 90 L 380 230 L 620 230 L 620 300 L 140 300 Z" class="boot-outline"/>

      <!-- c1r1 io capisco -->
      <text x="260" y="118" text-anchor="middle" class="boot-pron">io</text>
      <text x="260" y="142" text-anchor="middle" class="boot-form">cap<tspan class="boot-vowel">isc</tspan>o</text>
      <circle cx="168" cy="112" r="5" class="boot-marker-chg"/>
      <text x="260" y="156" text-anchor="middle" class="boot-tag-chg">▲ adds -isc-</text>

      <!-- c1r2 tu capisci -->
      <text x="260" y="188" text-anchor="middle" class="boot-pron">tu</text>
      <text x="260" y="212" text-anchor="middle" class="boot-form">cap<tspan class="boot-vowel">isc</tspan>i</text>
      <circle cx="168" cy="182" r="5" class="boot-marker-chg"/>
      <text x="260" y="226" text-anchor="middle" class="boot-tag-chg">▲ adds -isc-</text>

      <!-- c1r3 lui/lei capisce -->
      <text x="260" y="258" text-anchor="middle" class="boot-pron">lui / lei</text>
      <text x="260" y="282" text-anchor="middle" class="boot-form">cap<tspan class="boot-vowel">isc</tspan>e</text>
      <circle cx="168" cy="252" r="5" class="boot-marker-chg"/>
      <text x="260" y="296" text-anchor="middle" class="boot-tag-chg">▲ adds -isc-</text>

      <!-- c2r1 noi capiamo (outside boot) -->
      <text x="500" y="118" text-anchor="middle" class="boot-pron">noi</text>
      <text x="500" y="142" text-anchor="middle" class="boot-form">cap<tspan style="fill:#5f7a5f">i</tspan>amo</text>
      <circle cx="408" cy="112" r="5" class="boot-marker-reg"/>
      <text x="500" y="156" text-anchor="middle" class="boot-tag-reg">○ no -isc-</text>

      <!-- c2r2 voi capite (outside boot) -->
      <text x="500" y="188" text-anchor="middle" class="boot-pron">voi</text>
      <text x="500" y="212" text-anchor="middle" class="boot-form">cap<tspan style="fill:#5f7a5f">i</tspan>te</text>
      <circle cx="408" cy="182" r="5" class="boot-marker-reg"/>
      <text x="500" y="226" text-anchor="middle" class="boot-tag-reg">○ no -isc-</text>

      <!-- c2r3 loro capiscono (inside boot foot) -->
      <text x="500" y="258" text-anchor="middle" class="boot-pron">loro</text>
      <text x="500" y="282" text-anchor="middle" class="boot-form">cap<tspan class="boot-vowel">isc</tspan>ono</text>
      <circle cx="408" cy="252" r="5" class="boot-marker-chg"/>
      <text x="500" y="296" text-anchor="middle" class="boot-tag-chg">▲ adds -isc-</text>

      <!-- footer note on stress -->
      <text x="380" y="346" text-anchor="middle" class="boot-sub">Stress lands on the infix inside the boot (so -isc- appears); on the ending in noi / voi (so it drops out).</text>
      <text x="380" y="370" text-anchor="middle" class="boot-sub">Same shape for finire (finisco), preferire (preferisco) and pulire (pulisco). The endings never change.</text>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      The four -isc- forms (leg + foot) make a boot; noi / voi sit outside it with no infix.
    </figcaption>
  </figure>`;
}

// ---- Gender by noun ending ----------------------------------------------
//
// An A1 reference grid that sorts the reliable noun endings into two panels —
// typically il (masculine) and typically la (feminine) — each ending shown as a
// chip with a worked example, then a strip of high-frequency exceptions to learn
// against the rule. Gender is encoded three ways independent of colour: the
// article word il vs la, a square ▪ marker for masculine versus a round ● marker
// for feminine, and the two-panel split itself; exceptions carry a dashed badge.
function genderEndingPatterns() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 400" role="img"
         aria-labelledby="gender-title gender-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="gender-title">Noun gender by ending</title>
      <desc id="gender-desc">Two panels sorting Italian noun endings by their
        usual gender. The left panel, typically masculine and taking il, lists the
        endings -o as in il libro, -ore as in il colore, and -ma as in il problema.
        The right panel, typically feminine and taking la, lists -a as in la casa,
        -zione as in la stazione, -sione as in la decisione, -tà as in la città, and
        -tù as in la virtù. Masculine entries are marked with a square, feminine
        with a circle. A strip across the bottom flags common exceptions to learn
        against the rule: la mano, la foto, il poeta and il pilota.</desc>

      <style>
        .gd-panel-m { fill: #3f553f; font-size: 17px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .gd-panel-f { fill: #9a531f; font-size: 17px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .gd-panel-sub { font-size: 12px; font-family: Inter, system-ui, sans-serif; }
        .gd-chip-m { fill: #e7f0e7; stroke: #5f7a5f; stroke-width: 1.75; }
        .gd-chip-f { fill: #fbf0e6; stroke: #d9803a; stroke-width: 1.75; }
        .gd-end-m { fill: #3f553f; font-size: 16px; font-weight: 800; font-family: Inter, system-ui, sans-serif; }
        .gd-end-f { fill: #9a531f; font-size: 16px; font-weight: 800; font-family: Inter, system-ui, sans-serif; }
        .gd-eg { fill: #4a554a; font-size: 14px; font-family: Inter, system-ui, sans-serif; }
        .gd-eg-end { font-weight: 800; }
        .gd-marker-m { fill: #5f7a5f; }
        .gd-marker-f { fill: #b9682c; }
        .gd-divider { stroke: #e3e0d8; stroke-width: 1.5; stroke-dasharray: 4 5; }
        .gd-exc-box { fill: #fbfaf6; stroke: #b9682c; stroke-width: 1.5; stroke-dasharray: 7 5; }
        .gd-exc-title { fill: #9a531f; font-size: 13px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .gd-exc-item { fill: #4a554a; font-size: 14px; font-family: Inter, system-ui, sans-serif; }
        .gd-exc-art { font-weight: 800; }
      </style>

      <!-- MASCULINE panel -->
      <text x="40" y="38" class="gd-panel-m">▪ il — typically masculine</text>
      <text x="40" y="58" class="gd-panel-sub" fill="#5f7a5f">endings that point to a masculine noun</text>

      <!-- -o -->
      <rect x="40" y="78" width="74" height="40" rx="9" class="gd-chip-m"/>
      <text x="77" y="104" text-anchor="middle" class="gd-end-m">-o</text>
      <text x="128" y="104" class="gd-eg">il libr<tspan class="gd-eg-end" fill="#3f553f">o</tspan></text>
      <!-- -ore -->
      <rect x="40" y="128" width="74" height="40" rx="9" class="gd-chip-m"/>
      <text x="77" y="154" text-anchor="middle" class="gd-end-m">-ore</text>
      <text x="128" y="154" class="gd-eg">il col<tspan class="gd-eg-end" fill="#3f553f">ore</tspan></text>
      <!-- -ma -->
      <rect x="40" y="178" width="74" height="40" rx="9" class="gd-chip-m"/>
      <text x="77" y="204" text-anchor="middle" class="gd-end-m">-ma</text>
      <text x="128" y="204" class="gd-eg">il proble<tspan class="gd-eg-end" fill="#3f553f">ma</tspan> <tspan fill="#6b766b" font-size="11">(Greek -ma)</tspan></text>

      <!-- divider -->
      <line x1="380" y1="74" x2="380" y2="288" class="gd-divider"/>

      <!-- FEMININE panel -->
      <text x="720" y="38" text-anchor="end" class="gd-panel-f">la — typically feminine ●</text>
      <text x="720" y="58" text-anchor="end" class="gd-panel-sub" fill="#b9682c">endings that point to a feminine noun</text>

      <!-- -a -->
      <rect x="410" y="78" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="101" text-anchor="middle" class="gd-end-f">-a</text>
      <text x="500" y="101" class="gd-eg">la cas<tspan class="gd-eg-end" fill="#9a531f">a</tspan></text>
      <!-- -zione -->
      <rect x="410" y="120" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="143" text-anchor="middle" class="gd-end-f">-zione</text>
      <text x="500" y="143" class="gd-eg">la sta<tspan class="gd-eg-end" fill="#9a531f">zione</tspan></text>
      <!-- -sione -->
      <rect x="410" y="162" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="185" text-anchor="middle" class="gd-end-f">-sione</text>
      <text x="500" y="185" class="gd-eg">la deci<tspan class="gd-eg-end" fill="#9a531f">sione</tspan></text>
      <!-- -tà -->
      <rect x="410" y="204" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="227" text-anchor="middle" class="gd-end-f">-tà</text>
      <text x="500" y="227" class="gd-eg">la cit<tspan class="gd-eg-end" fill="#9a531f">tà</tspan></text>
      <!-- -tù -->
      <rect x="410" y="246" width="78" height="34" rx="9" class="gd-chip-f"/>
      <text x="449" y="269" text-anchor="middle" class="gd-end-f">-tù</text>
      <text x="500" y="269" class="gd-eg">la vir<tspan class="gd-eg-end" fill="#9a531f">tù</tspan></text>

      <!-- EXCEPTIONS strip -->
      <rect x="40" y="306" width="680" height="74" rx="12" class="gd-exc-box"/>
      <text x="60" y="332" class="gd-exc-title">Learn these against the rule:</text>
      <text x="60" y="360" class="gd-exc-item"><tspan class="gd-exc-art" fill="#9a531f">la</tspan> mano &nbsp;·&nbsp; <tspan class="gd-exc-art" fill="#9a531f">la</tspan> foto &nbsp;·&nbsp; <tspan class="gd-exc-art" fill="#3f553f">il</tspan> poeta &nbsp;·&nbsp; <tspan class="gd-exc-art" fill="#3f553f">il</tspan> pilota</text>
    </svg>
    <figcaption class="mt-2 text-center text-xs text-sage-500">
      Endings give the gender most of the time — -o / -ore / -ma lean il, -a / -zione / -sione / -tà / -tù lean la — but a few common nouns break the pattern, and nouns in -e go either way.
    </figcaption>
  </figure>`;
}

// ---- Forgetting curve vs spaced review ----------------------------------
//
// The flagship figure for the "How to Learn" page. Two memory-over-time curves:
// a dashed curve for a single exposure (decays fast to near-nothing), and a
// solid curve reviewed at widening intervals (each review resets retention high
// and the decay gets gentler). Colour is never the only cue — the single-look
// curve is dashed and labelled, the reviewed curve is solid with amber review
// pins, and text labels carry the meaning independently.
function forgettingCurve() {
  return `
  <figure class="article-figure my-7">
    <svg viewBox="0 0 760 320" role="img"
         aria-labelledby="fc-title fc-desc"
         class="w-full h-auto rounded-2xl border border-sage-100 bg-white"
         xmlns="http://www.w3.org/2000/svg">
      <title id="fc-title">The forgetting curve and how spaced review beats it</title>
      <desc id="fc-desc">A graph of memory strength over time. A dashed line shows
        a single study session: memory falls steeply and is almost gone within a
        week. A solid green line shows the same material reviewed at widening
        intervals — day one, two, four, a week, two weeks — where each amber
        review point lifts memory back near full and the line decays more slowly
        each time, staying high after a month.</desc>
      <style>
        .fc-axis { stroke: #9ca89c; stroke-width: 2; }
        .fc-grid { stroke: #e6ece6; stroke-width: 1; }
        .fc-label { fill: #6b766b; font-size: 13px; font-family: Inter, system-ui, sans-serif; }
        .fc-single { fill: none; stroke: #b9682c; stroke-width: 2.5; stroke-dasharray: 7 6; opacity: 0.85; }
        .fc-spaced { fill: none; stroke: #5f7a5f; stroke-width: 3; }
        .fc-pin { fill: #d9803a; stroke: #ffffff; stroke-width: 2; }
        .fc-single-label { fill: #9a531f; font-size: 13.5px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .fc-spaced-label { fill: #3f553f; font-size: 13.5px; font-weight: 700; font-family: Inter, system-ui, sans-serif; }
        .fc-axis-title { fill: #6b766b; font-size: 12px; font-family: Inter, system-ui, sans-serif; }
      </style>

      <!-- Axes -->
      <line class="fc-axis" x1="64" y1="40" x2="64" y2="262" />
      <line class="fc-axis" x1="64" y1="262" x2="724" y2="262" />
      <line class="fc-grid" x1="64" y1="60" x2="724" y2="60" />
      <text class="fc-axis-title" x="10" y="54" transform="rotate(-90 16 150)">Memory</text>

      <!-- Time ticks -->
      <text class="fc-label" x="64"  y="282" text-anchor="middle">Day 1</text>
      <text class="fc-label" x="180" y="282" text-anchor="middle">2</text>
      <text class="fc-label" x="320" y="282" text-anchor="middle">4</text>
      <text class="fc-label" x="470" y="282" text-anchor="middle">1 wk</text>
      <text class="fc-label" x="600" y="282" text-anchor="middle">2 wk</text>
      <text class="fc-label" x="708" y="282" text-anchor="middle">1 mo</text>

      <!-- Single exposure: steep decay to the floor -->
      <path class="fc-single" d="M64,60 C110,150 150,210 210,238 S360,258 724,258" />
      <text class="fc-single-label" x="250" y="232">One look — gone in days</text>

      <!-- Spaced review: sawtooth resets, each decay gentler -->
      <path class="fc-spaced" d="M64,60
        C100,120 140,150 180,150
        L180,66
        C230,108 280,132 320,134
        L320,62
        C390,96 430,112 470,114
        L470,60
        C540,82 570,92 600,94
        L600,58
        C650,70 690,76 724,80" />

      <!-- Review pins at each interval -->
      <circle class="fc-pin" cx="180" cy="66" r="5.5" />
      <circle class="fc-pin" cx="320" cy="62" r="5.5" />
      <circle class="fc-pin" cx="470" cy="60" r="5.5" />
      <circle class="fc-pin" cx="600" cy="58" r="5.5" />
      <text class="fc-spaced-label" x="486" y="44">Spaced review — it stays</text>
    </svg>
    <figcaption class="text-xs text-sage-500 mt-2 text-center">Each review is timed for just before you'd forget — that's when memory consolidates.</figcaption>
  </figure>`;
}

export const FIGURES = {
  "forgetting-curve": forgettingCurve,
  "preterite-imperfect-timeline": preteriteImperfectTimeline,
  "por-para-arrows": porParaArrows,
  "ser-estar-essence-state": serEstarEssenceState,
  "subjunctive-trigger-landscape": subjunctiveTriggerLandscape,
  "stem-change-boot": stemChangeBoot,
  "gender-ending-patterns": genderEndingPatterns,
};

// Inject any registered figures into a freshly-rendered article body. Unknown
// ids are left as an empty slot (no crash) so a typo can't blank the article.
export function hydrateFigures(root) {
  root.querySelectorAll("[data-figure]").forEach((slot) => {
    const fn = FIGURES[slot.dataset.figure];
    if (fn) slot.innerHTML = fn();
  });
}
